import type {
    CSSProperties,
    FC,
    MouseEvent,
    ReactNode
} from 'react'

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollTrigger, useGSAP } from '@/lib/motion'
import { useLenis, useScrollTo } from '@/components/landing/smooth-scroll'
import SocialLinks from '@/components/landing/social-links'
import Sparkle from '@/components/landing/decor/sparkle'
import { NAV_ITEMS } from '@/constants/content'

// Scroll position from which a nav item counts as the current one.
const getActivation = (id: string): number => {
    if (id === 'top') return 0

    const element = document.getElementById(id)

    if (!element) return Number.POSITIVE_INFINITY

    const track = element.closest<HTMLElement>('[data-scroll-track]')
    const from = Number.parseFloat(element.dataset.activeFrom ?? '')

    if (track && !Number.isNaN(from) && window.matchMedia('(min-width: 1024px)').matches) {
        const top = track.getBoundingClientRect().top + window.scrollY

        return top + (track.offsetHeight - window.innerHeight) * from
    }

    return element.getBoundingClientRect().top + window.scrollY
}

interface SiteHeaderProps {
    // 'home' scrolls within the one-page site; 'works' is the separate WORKS archive page,
    // whose links lead back to the sections of the top page.
    page?: 'home' | 'works'
}

const SiteHeader: FC<SiteHeaderProps> = ({ page = 'home' }): ReactNode => {
    const headerRef = useRef<HTMLElement>(null)
    const railRef = useRef<HTMLDivElement>(null)
    const menuButtonRef = useRef<HTMLButtonElement>(null)
    const firstLinkRef = useRef<HTMLAnchorElement>(null)
    const wasOpenRef = useRef(false)

    const isHome = page === 'home'

    const [active, setActive] = useState(isHome ? 'top' : page)
    const [dark, setDark] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const scrollTo = useScrollTo()
    const lenis = useLenis()

    const hrefFor = (id: string): string => {
        if (isHome) return `#${id}`
        if (id === 'top') return '/'
        if (id === page) return `/${page}`

        return `/#${id}`
    }

    useGSAP(() => {
        if (!isHome) {
            ScrollTrigger.create({
                start: 0,
                end: 'max',
                onUpdate: self => railRef.current?.style.setProperty('--progress', self.progress.toFixed(4))
            })

            return
        }

        let targets = NAV_ITEMS.map(item => ({ id: item.id, top: getActivation(item.id) }))

        const onRefresh = (): void => {
            targets = NAV_ITEMS.map(item => ({ id: item.id, top: getActivation(item.id) }))
        }

        ScrollTrigger.addEventListener('refresh', onRefresh)

        let current = 'top'

        ScrollTrigger.create({
            start: 0,
            end: 'max',
            onUpdate: self => {
                railRef.current?.style.setProperty('--progress', self.progress.toFixed(4))

                const probe = self.scroll() + window.innerHeight * 0.4
                let next = 'top'

                for (const target of targets) {
                    if (target.top <= probe) next = target.id
                }

                if (next !== current) {
                    current = next
                    setActive(next)
                }
            }
        })

        const contact = document.getElementById('contact')

        if (contact) {
            ScrollTrigger.create({
                trigger: contact,
                start: 'top 64px',
                end: 'bottom top',
                onToggle: self => setDark(self.isActive)
            })
        }

        return () => ScrollTrigger.removeEventListener('refresh', onRefresh)
    }, { scope: headerRef })

    const closeMenu = useCallback(() => setMenuOpen(false), [])

    useEffect(() => {
        if (menuOpen) {
            wasOpenRef.current = true
            lenis?.stop()
            firstLinkRef.current?.focus()

            const onKey = (event: KeyboardEvent): void => {
                if (event.key === 'Escape') setMenuOpen(false)
            }

            window.addEventListener('keydown', onKey)

            return () => window.removeEventListener('keydown', onKey)
        }

        lenis?.start()

        if (wasOpenRef.current) {
            wasOpenRef.current = false
            menuButtonRef.current?.focus()
        }
    }, [menuOpen, lenis])

    const handleNavigate = (id: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
        // On another page every link except the current one is a normal page load.
        if (!isHome && id !== page) {
            setMenuOpen(false)

            return
        }

        event.preventDefault()

        if (!isHome) {
            setMenuOpen(false)
            window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })

            return
        }

        if (menuOpen) {
            setMenuOpen(false)
            window.setTimeout(() => scrollTo(id), 80)

            return
        }

        scrollTo(id)
    }

    return (
        <>
            <header
                ref={headerRef}
                data-site-header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-colors duration-700',
                    dark ? 'text-mist' : 'text-ink'
                )}
            >
                <div className='flex h-16 items-center justify-between gap-3 px-4 sm:px-5 lg:h-20 lg:px-10'>
                    <div className='flex min-w-0 items-center gap-4 xl:gap-5'>
                        <a
                            href={hrefFor('top')}
                            onClick={handleNavigate('top')}
                            className='group flex shrink-0 items-center gap-2 rounded-full py-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold sm:gap-2.5'
                        >
                            <Sparkle className='size-4 text-gold transition-transform duration-700 ease-[var(--ease-cinema)] group-hover:rotate-90' />
                            {/* Between 1024 and 1280px only the ✦ mark shows, so the nine nav items fit. */}
                            <span className='font-heading text-lg leading-none tracking-[0.2em] sm:text-xl sm:tracking-[0.24em] lg:sr-only xl:not-sr-only'>
                                AMAMA NIA
                            </span>
                        </a>

                        <span
                            aria-hidden='true'
                            className={cn(
                                'hidden h-6 w-px transition-colors duration-700 lg:block',
                                dark ? 'bg-mist/20' : 'bg-ink/15'
                            )}
                        />

                        <SocialLinks dark={dark} className='hidden lg:flex' />
                    </div>

                    <nav aria-label='メインナビゲーション' className='hidden lg:block'>
                        <ul
                            className={cn(
                                'flex items-center gap-0.5 rounded-full px-2 py-1.5 ring-1 backdrop-blur-md transition-colors duration-700',
                                dark ? 'bg-night-soft/50 ring-mist/15' : 'bg-mist/55 ring-ink/10'
                            )}
                        >
                            {NAV_ITEMS.filter(item => item.id !== 'top').map(item => {
                                const isActive = active === item.id

                                return (
                                    <li key={item.id}>
                                        <a
                                            href={hrefFor(item.id)}
                                            onClick={handleNavigate(item.id)}
                                            aria-current={isActive ? 'location' : undefined}
                                            className={cn(
                                                'relative flex items-center gap-1.5 rounded-full px-2.5 py-2 font-mono xl:px-3.5 text-[11px] tracking-[0.2em] transition-colors duration-500 focus-visible:outline-2 focus-visible:outline-gold',
                                                isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                                            )}
                                        >
                                            <Sparkle
                                                className={cn(
                                                    'size-2 text-gold transition-all duration-500',
                                                    isActive ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                                                )}
                                            />
                                            {item.label}
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex shrink-0 items-center gap-1.5 lg:hidden'>
                        <SocialLinks dark={dark} compact />

                        <button
                            ref={menuButtonRef}
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label='メニューを開く'
                            className={cn(
                                'flex size-10 items-center justify-center gap-2 rounded-full ring-1 backdrop-blur-md sm:h-11 sm:w-auto sm:px-4 focus-visible:outline-2 focus-visible:outline-gold',
                                dark ? 'bg-night-soft/50 ring-mist/15' : 'bg-mist/60 ring-ink/10'
                            )}
                        >
                            <Menu className='size-4' />
                            <span aria-hidden='true' className='hidden font-mono text-[11px] tracking-[0.24em] sm:inline'>MENU</span>
                        </button>
                    </div>
                </div>
            </header>

            <div
                ref={railRef}
                aria-hidden='true'
                className={cn(
                    'pointer-events-none fixed right-7 top-1/2 z-40 hidden h-[30vh] w-px -translate-y-1/2 transition-colors duration-700 lg:block',
                    dark ? 'bg-mist/15' : 'bg-ink/12'
                )}
                style={{ '--progress': 0 } as CSSProperties}
            >
                <span
                    className={cn('absolute inset-0 origin-top', dark ? 'bg-mist/60' : 'bg-ink/50')}
                    style={{ transform: 'scaleY(var(--progress))' }}
                />
                <span className='absolute left-1/2 size-3 -translate-x-1/2 -translate-y-1/2' style={{ top: 'calc(var(--progress) * 100%)' }}>
                    <Sparkle className='size-3 text-gold' />
                </span>
            </div>

            <div
                id='mobile-menu'
                role='dialog'
                aria-modal='true'
                aria-label='メニュー'
                className={cn(
                    'fixed inset-0 z-[60] flex flex-col bg-paper/95 text-ink backdrop-blur-xl transition-[opacity,visibility] duration-500 lg:hidden',
                    menuOpen ? 'visible opacity-100' : 'invisible opacity-0'
                )}
            >
                <div className='flex h-16 items-center justify-between px-5'>
                    <span className='flex items-center gap-2.5'>
                        <Sparkle className='size-4 text-gold' />
                        <span className='font-heading text-xl tracking-[0.24em]'>AMAMA NIA</span>
                    </span>
                    <button
                        type='button'
                        onClick={closeMenu}
                        aria-label='メニューを閉じる'
                        className='flex size-11 items-center justify-center rounded-full ring-1 ring-ink/10 focus-visible:outline-2 focus-visible:outline-gold'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <nav aria-label='モバイルナビゲーション' className='flex flex-1 flex-col justify-center px-8'>
                    <ol className='flex flex-col gap-1'>
                        {NAV_ITEMS.map((item, index) => (
                            <li key={item.id}>
                                <a
                                    ref={index === 0 ? firstLinkRef : undefined}
                                    href={hrefFor(item.id)}
                                    onClick={handleNavigate(item.id)}
                                    aria-current={active === item.id ? 'location' : undefined}
                                    className='group flex items-baseline gap-4 rounded-lg py-2 focus-visible:outline-2 focus-visible:outline-gold'
                                >
                                    <span className='w-6 font-mono text-[11px] text-ink-soft'>
                                        {String(index).padStart(2, '0')}
                                    </span>
                                    <span className={cn('font-heading text-4xl tracking-[0.06em]', active === item.id && 'italic')}>
                                        {item.label}
                                    </span>
                                    <span className='text-xs tracking-[0.2em] text-ink-soft'>{item.ja}</span>
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <div className='flex items-center justify-between gap-4 px-8 pb-10'>
                    <p className='flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-ink-soft'>
                        <Sparkle className='size-2.5 text-gold' />
                        OBSERVATION LOG — SUMMER
                    </p>
                    <SocialLinks />
                </div>
            </div>
        </>
    )
}

export default SiteHeader
